/**
 * Login page component
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

// Ensure axios base URL is set
if (!axios.defaults.baseURL) {
  axios.defaults.baseURL = process.env.NODE_ENV === 'production' 
    ? 'https://hotbump.online/api' 
    : 'http://localhost:8010/api';
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '1071781435371-mv4cvver534k8sk3d6clf9r5ktcfju5f.apps.googleusercontent.com';

function Login() {
  const { login, language } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const translations = {
    en: {
      title: 'Login',
      email: 'Email',
      password: 'Password',
      login: 'Login',
      register: "Don't have an account? Register",
      googleLogin: 'Login with Google',
      error: 'Login failed'
    },
    ru: {
      title: 'Вход',
      email: 'Email',
      password: 'Пароль',
      login: 'Войти',
      register: 'Нет аккаунта? Зарегистрироваться',
      googleLogin: 'Войти через Google',
      error: 'Ошибка входа'
    }
  };

  const t = translations[language] || translations.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      login(response.data.access_token, response.data.user);
      navigate('/web/explore');
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      if (!GOOGLE_CLIENT_ID) {
        throw new Error('Google client_id is not configured');
      }

      // Load Google OAuth script if not already loaded
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(reject, 10000); // 10 second timeout
        });
      }

      // Initialize Google OAuth
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            // Send token to backend
            const result = await axios.post('/auth/oauth/google', {
              token: response.credential
            });

            login(result.data.access_token, result.data.user);
            navigate('/web/explore');
          } catch (err) {
            setError(err.response?.data?.detail || t.error);
            setLoading(false);
          }
        }
      });

      // Trigger Google sign-in
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback: show popup
          window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'email profile',
            callback: async (tokenResponse) => {
              try {
                const result = await axios.post('/auth/oauth/google', {
                  token: tokenResponse.access_token
                });
                login(result.data.access_token, result.data.user);
                navigate('/web/explore');
              } catch (err) {
                setError(err.response?.data?.detail || t.error);
                setLoading(false);
              }
            }
          }).requestAccessToken();
        }
        setLoading(false);
      });
    } catch (err) {
      console.error('Google OAuth error:', err);
      setError(language === 'en' ? 'Failed to load Google OAuth' : 'Ошибка загрузки Google OAuth');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-dark-900 to-burgundy-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-dark-800 border border-burgundy-800 rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-white mb-6">{t.title}</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-900 text-red-200 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.email}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 bg-dark-700 border border-burgundy-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t.password}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 bg-dark-700 border border-burgundy-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-burgundy-600 hover:bg-burgundy-700 text-white font-medium rounded-md disabled:opacity-50"
          >
            {loading ? 'Loading...' : t.login}
          </button>
        </form>

        <div className="mt-4">
          <button
            onClick={handleGoogleLogin}
            className="w-full py-2 px-4 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-md flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{t.googleLogin}</span>
          </button>
        </div>

        <div className="mt-6 text-center">
          <Link to="/web/register" className="text-blue-400 hover:text-blue-300">
            {t.register}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;

